import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { firstValueFrom, fromEvent, Observable, takeUntil } from 'rxjs';
import { AppHttpService } from './app-http.service';
import { CapturedHereRow, MapFeatureLink } from '../models/map-feature-link';
import type { FeatureLinkSummary } from '../helpers/feature-link-status';

export type ProjectMapAssetKind = 'geojson' | 'raster_xyz' | 'feature_index';
export type ProjectMapAssetRole = 'geometry' | 'labels' | 'inspection';
export interface ProjectMapAsset { id: string; sha256: string; bytes: number; kind: ProjectMapAssetKind; role: ProjectMapAssetRole; contentId: string; tileSize?: 256 | 512; minZoom?: number; maxZoom?: number; renditionId?: string; }
export interface ProjectMapSymbol { geometry: 'point' | 'line' | 'polygon'; color: string; strokeColor?: string; strokeWidth?: number; size?: number; dash?: number[]; fillAlpha?: number; }
export interface ProjectMapStyle { symbols: ProjectMapSymbol[]; uniqueValues?: { field: string; values: { value: string | number; symbol: ProjectMapSymbol }[] }; label?: { field: string; minZoom: number; maxZoom: number; color: string; size: number }; legend: { label: string; symbol: ProjectMapSymbol }[]; popupFields: { field: string; label: string }[]; }
export interface PublishedMapLayerDrawing { sheetUuid: string; revisionUuid: string; number: string; label: string | null; needsPlacement: boolean; }
export interface PublishedMapLayer { id: string; name: string; groupId: string | null; sourceVersionId: string; sublayerKey: string; order: number; bounds: [number, number, number, number]; defaultVisible: boolean; defaultOpacity: number; labelDefault: boolean; minZoom: number; maxZoom: number; sourceCapturedAt: string; attribution: string; styleVersion: string; style: ProjectMapStyle; status: 'published' | 'superseded'; offline: 'allowed' | 'online_only'; inspect: 'full' | 'view_only'; assets: ProjectMapAsset[];
  /** A placed drawing (Drawings slice 4): the sheet revision it shows; needsPlacement once a newer revision is issued (R21). */
  drawing?: PublishedMapLayerDrawing; }
export interface ProjectMapManifest { schemaVersion: 1; projectId: string; catalogRevision: number; groups: { id: string; name: string; order: number }[]; layers: PublishedMapLayer[]; capabilities: { manage: boolean; publish: boolean; download: boolean }; }
export interface ProjectMapFeatureResponse {
  // A prepared (unpublished) rendition answers with renditionId and null catalog fields.
  catalogRevision: number | null; layerId: string | null; renditionId?: string; sourceVersionId: string; sublayerKey: string;
  features: { id: string; geometry: unknown; properties: Record<string, unknown> }[];
  nextCursor: string | null; truncated: boolean;
  /**
   * Published layers are answered live-first: 'live' from the layer's live source (ArcGIS), else 'snapshot'.
   * `sourceFallbackReason` is set when a live attempt failed. Same ids and shape either way. Older api builds omit both.
   */
  source?: 'live' | 'snapshot';
  sourceFallbackReason?: string;
}
export interface ProjectMapPreferences { version: number; overrides: Record<string, Partial<{ visible: boolean; opacity: number; labels: boolean }>>; }
export type ProjectMapPreferencePatch = Record<string, ProjectMapPreferences['overrides'][string] | null>;
export interface PublishedAssetGrant { asset: ProjectMapAsset; url?: string; grant?: string; expiresIn: number; }

/**
 * "Zoom to" on a layer fits its stored extent, but a published raster
 * rendition is only prepared for its layer's published zoom range (e.g. a
 * GeoPDF sheet at z14–16) — landing past `maxZoom` makes the layer vanish
 * exactly when the user asked to see it. Returns the zoom to clamp to when
 * `currentZoom` overshoots the layer's `maxZoom`, or `null` when no clamp is
 * needed (already in range, or a legacy layer with no published zoom range
 * to clamp against).
 */
export function clampZoomToPublishedMax(currentZoom: number, published: PublishedMapLayer | undefined): number | null {
  if (!published || !Number.isFinite(published.maxZoom)) return null;
  if (currentZoom <= published.maxZoom) return null;
  return published.maxZoom;
}

@Injectable({ providedIn: 'root' })
export class ProjectMapManifestService {
  constructor(private http: AppHttpService) {}

  private request<T>(request: Observable<T>, signal?: AbortSignal): Promise<T> {
    signal?.throwIfAborted();
    return firstValueFrom(signal ? request.pipe(takeUntil(fromEvent(signal, 'abort'))) : request);
  }

  async manifest(projectId: string, etag?: string, signal?: AbortSignal): Promise<{ manifest: ProjectMapManifest | null; etag?: string; notModified: boolean }> {
    try {
      const response = await this.request(this.http.getService(`/v1/project/${projectId}/map-layers/manifest`, {}, {
        observe: 'response', headers: etag ? { 'If-None-Match': etag } : undefined,
      }), signal) as HttpResponse<ProjectMapManifest>;
      const manifest = response.body as ProjectMapManifest;
      if (!manifest || manifest.schemaVersion !== 1 || manifest.projectId !== projectId) throw new Error('Unsupported published map catalog');
      return { manifest, etag: response.headers.get('ETag') || undefined, notModified: false };
    } catch (error) {
      if (error?.status === 304 && etag) return { manifest: null, etag, notModified: true };
      throw error;
    }
  }

  async preferences(projectId: string, signal?: AbortSignal): Promise<ProjectMapPreferences> {
    return this.request(this.http.getService(`/v1/project/${projectId}/map-layers/preferences`), signal);
  }

  async patchPreferences(projectId: string, expectedVersion: number, overrides: ProjectMapPreferencePatch, signal?: AbortSignal): Promise<ProjectMapPreferences> {
    return this.request(this.http.patchService(`/v1/project/${projectId}/map-layers/preferences`, { expectedVersion, overrides }), signal);
  }

  async grant(projectId: string, layerId: string, renditionId: string, catalogRevision: number, signal?: AbortSignal): Promise<PublishedAssetGrant> {
    return this.request(this.http.postService(`/v1/project/${projectId}/map-layers/${layerId}/renditions/${renditionId}/grant`, { catalogRevision }), signal);
  }

  /** `cursor`: the previous page's `nextCursor`. */
  async features(projectId: string, layerId: string, catalogRevision: number, bbox: number[], limit: number, signal?: AbortSignal,
    cursor?: string | null): Promise<ProjectMapFeatureResponse> {
    return this.request(this.http.getService(`/v1/project/${encodeURIComponent(projectId)}/map-layers/${encodeURIComponent(layerId)}/features`,
      { catalogRevision, bbox, limit, ...(cursor ? { cursor } : {}) }), signal);
  }

  /** Same lookup as `features`, for a rendition still under review in the drawer. */
  async renditionFeatures(projectId: string, renditionId: string, bbox: number[], limit: number, signal?: AbortSignal): Promise<ProjectMapFeatureResponse> {
    return this.request(this.http.getService(`/v1/project/${encodeURIComponent(projectId)}/map-renditions/${encodeURIComponent(renditionId)}/features`,
      { bbox, limit }), signal);
  }

  /**
   * "Captured here" (spec §4.5): records linked to one feature, newest first, ACL-filtered by the api.
   * `meta.totalRecords` counts at most 200 rows (api cap), so 200 means "200+".
   */
  async links(projectId: string, layerId: string, featureId: string,
    params: { catalogRevision: number; sourceVersionId: string; sublayerKey: string; start?: number; total?: number },
    signal?: AbortSignal): Promise<{ data: CapturedHereRow[]; meta: { totalRecords: number } }> {
    const response = await this.request(this.http.getService(
      `/v1/project/${encodeURIComponent(projectId)}/map-layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(featureId)}/links`,
      { catalogRevision: params.catalogRevision, sourceVersionId: params.sourceVersionId, sublayerKey: params.sublayerKey,
        start: params.start ?? 0, total: params.total ?? 6 }), signal) as { data: CapturedHereRow[]; meta: { totalRecords: number } };
    const data = response?.data || [];
    return { data, meta: { totalRecords: response?.meta?.totalRecords ?? data.length } };
  }

  /**
   * Records linked to each feature of a layer, newest first, ACL-filtered like "Captured here" (Addendum 3: done vs
   * pending on the launcher map). `featureIds` limits the answer; without it every linked feature of the layer.
   */
  async featureLinkSummary(projectId: string, layerId: string, featureIds?: string[] | null, signal?: AbortSignal): Promise<FeatureLinkSummary> {
    const response = await this.request(this.http.getService(
      `/v1/project/${encodeURIComponent(projectId)}/map-layers/${encodeURIComponent(layerId)}/feature-link-summary`,
      featureIds?.length ? { featureId: featureIds } : {}), signal) as FeatureLinkSummary;
    const features = response?.features && typeof response.features === 'object' ? response.features : {};
    return { layerId: response?.layerId ?? layerId, features };
  }

  /**
   * Re-enqueue a failed (or never rendered) design snapshot; returns the link with `snapshot.status: 'pending'`.
   * The api answers 409 (already pending/ready), 410, 503 (renderer not configured); PM / lead inspector only.
   */
  async retrySnapshot(projectId: string, linkUuid: string, signal?: AbortSignal): Promise<MapFeatureLink> {
    const response = await this.request(this.http.postService(
      `/v1/project/${encodeURIComponent(projectId)}/map-feature-links/${encodeURIComponent(linkUuid)}/snapshot`, {}), signal) as { data: MapFeatureLink };
    return response.data;
  }

  /**
   * One saved link as the api serializes it now (the control polls it while its snapshot renders).
   * The api answers 403 without map-layer read and 404 for an unknown or unreadable link.
   */
  async link(projectId: string, linkUuid: string, signal?: AbortSignal): Promise<MapFeatureLink> {
    const response = await this.request(this.http.getService(
      `/v1/project/${encodeURIComponent(projectId)}/map-feature-links/${encodeURIComponent(linkUuid)}`), signal) as { data: MapFeatureLink };
    return response.data;
  }

  tileUrl(grant: string, z: string | number, x: string | number, y: string | number): string {
    // This gateway is intentionally token-free: the short-lived signed grant is
    // the complete authorization for ArcGIS tile requests and is held in memory.
    return `${this.http.API_ENDPOINT}/v1/project/map-tiles/${z}/${x}/${y}.png?grant=${encodeURIComponent(grant)}`;
  }
}
